import { useEffect, useRef } from "react";
import { Trash2 } from "lucide-react";

// Minimal in-app signature pad — pointer events with pointer capture, no
// external lib. Works reliably on touch + mouse + stylus on Chrome/Safari.
const SignaturePad = ({ open, onChange, testid = "signature-pad" }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  // Re-size + reset the canvas backing store whenever the dialog is (re)opened
  // — the very first mount can happen while the modal is still animating in
  // and getBoundingClientRect() returns 0, killing all input math.
  useEffect(() => {
    if (!open) return undefined;
    let raf = 0;
    const setup = () => {
      const c = canvasRef.current;
      if (!c) return;
      const rect = c.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        raf = requestAnimationFrame(setup);
        return;
      }
      const ratio = window.devicePixelRatio || 1;
      c.width = Math.round(rect.width * ratio);
      c.height = Math.round(rect.height * ratio);
      const ctx = c.getContext("2d");
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0f172a";
      // Clear any prior strokes.
      ctx.clearRect(0, 0, c.width, c.height);
      onChange?.("");
    };
    raf = requestAnimationFrame(setup);
    return () => cancelAnimationFrame(raf);
  }, [open, onChange]);

  const pos = (ev) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: ev.clientX - r.left, y: ev.clientY - r.top };
  };

  const start = (ev) => {
    ev.preventDefault();
    drawing.current = true;
    // Keep receiving events even if the finger leaves the canvas box.
    try { ev.currentTarget.setPointerCapture(ev.pointerId); } catch (_) { /* ignore */ }
    last.current = pos(ev);
    // Draw a single dot so a tap without drag still leaves a mark.
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.arc(last.current.x, last.current.y, 1.1, 0, Math.PI * 2);
    ctx.fillStyle = "#0f172a";
    ctx.fill();
  };
  const move = (ev) => {
    if (!drawing.current) return;
    ev.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(ev);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = (ev) => {
    if (!drawing.current) return;
    drawing.current = false;
    try { ev?.currentTarget?.releasePointerCapture?.(ev.pointerId); } catch (_) { /* ignore */ }
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    onChange?.("");
  };

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-white/20 bg-white">
        <canvas
          ref={canvasRef}
          data-testid={testid}
          // touch-none disables browser gestures (scroll/zoom) on the canvas
          // so pointer events flow freely to us.
          className="block h-32 w-full touch-none rounded-md sm:h-40"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
        />
      </div>
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Sign inside the box</p>
        <button
          type="button"
          onClick={clear}
          data-testid={`${testid}-clear`}
          className="inline-flex items-center gap-1 rounded border border-white/15 bg-white/5 px-2 py-1 text-xs text-white/70 hover:border-white/25 hover:text-white"
        >
          <Trash2 className="h-3 w-3" /> Clear
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
